import { useState, useRef, useEffect, useMemo } from "react";
import { Buttons } from "../assets/asset";
import { Mic, Square, Bot } from "lucide-react";

const ExplanationModal = ({ isOpen, onClose, explanation }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionCache, setConversionCache] = useState(new Map());
  const speechSynthesis = window.speechSynthesis;
  const utteranceRef = useRef(null);

  const OPENAI_API_KEY = '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';
  const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

  const generateCacheKey = (text) => {
    let hash = 0;
    if (text.length === 0) return hash.toString();
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  };

  const getCachedResult = useMemo(() => {
    const cacheKey = generateCacheKey(explanation || '');
    return conversionCache.get(cacheKey);
  }, [explanation, conversionCache]);

  const setCachedResult = (text, result) => {
    const cacheKey = generateCacheKey(text);
    setConversionCache(prev => new Map(prev.set(cacheKey, result)));
  };

  useEffect(() => {
    const loadVoices = () => {
      const voices = speechSynthesis.getVoices();
      const australianFemaleVoices = voices.filter(voice => 
        (voice.lang.includes('en-AU') || 
         voice.name.toLowerCase().includes('australian') ||
         voice.name.toLowerCase().includes('australia')) &&
        (voice.name.toLowerCase().includes('female') || 
         voice.name.toLowerCase().includes('woman') ||
         voice.name.toLowerCase().includes('karen') ||
         voice.name.toLowerCase().includes('catherine'))
      );
      
      const englishFemaleVoices = voices.filter(voice => 
        voice.lang.startsWith('en-') &&
        (voice.name.toLowerCase().includes('female') || 
         voice.name.toLowerCase().includes('woman') ||
         voice.name.toLowerCase().includes('samantha') ||
         voice.name.toLowerCase().includes('alex') ||
         voice.name.toLowerCase().includes('victoria'))
      );
      
      const anyFemaleVoices = voices.filter(voice => 
        voice.name.toLowerCase().includes('female') || 
        voice.name.toLowerCase().includes('woman')
      );
      
      setAvailableVoices(voices);
      
      let preferredVoice = australianFemaleVoices[0] || 
                          englishFemaleVoices[0] || 
                          anyFemaleVoices[0] || 
                          voices[0];
      
      setSelectedVoice(preferredVoice);
    };

    loadVoices();
    speechSynthesis.addEventListener('voiceschanged', loadVoices);
    
    return () => {
      speechSynthesis.removeEventListener('voiceschanged', loadVoices);
    };
  }, []);

  const convertWithOpenAI = async (text) => {
    try {
      const response = await fetch(OPENAI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: `You are a helpful assistant that converts mathematical text to natural speech for foundation to year 9 students (ages 5-15). 

RULES:
1. Convert ALL mathematical symbols and expressions to natural, spoken English
2. Use simple, kid-friendly language
3. Convert parentheses to "open bracket" and "close bracket"
4. Convert mathematical operators: + = "plus", - = "minus", × or * = "times", ÷ or / = "divided by", = = "equals"
5. Convert numbers 1-20 to words (1="one", 2="two", etc.)
6. Handle Unicode math characters like 𝑥 as regular "x"
7. Add spaces between variables (ab = "a times b", 3x = "three x")
8. Keep the original sentence structure but make ALL math parts speakable
9. Don't add extra explanations, just convert the math to speech

Examples:
- "a(b+c)=ab+ac" → "a open bracket b plus c close bracket equals a times b plus a times c"
- "3𝑥 + 18" → "three x plus eighteen"
- "2x² = 8" → "two x squared equals eight"`
            },
            {
              role: 'user',
              content: `Convert this mathematical text to natural speech: "${text}"`
            }
          ],
          max_tokens: 200,
          temperature: 0.1
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      const convertedText = data.choices[0].message.content.trim();
      
      return convertedText;
    } catch (error) {
      throw error;
    }
  };

  const convertWithFallback = (text) => {
    let converted = text;
    
    const unicodeMap = {
      '𝑣': 'x', '𝑦': 'y', '𝑧': 'z', '𝑎': 'a', '𝑏': 'b', '𝑐': 'c',
      '𝑑': 'd', '𝑒': 'e', '𝑓': 'f', '𝑔': 'g', '𝑛': 'n', '𝑚': 'm'
    };
    
    Object.keys(unicodeMap).forEach(unicode => {
      converted = converted.replace(new RegExp(unicode, 'g'), unicodeMap[unicode]);
    });

    const symbolMap = {
      '=': ' equals ',
      '+': ' plus ',
      '-': ' minus ',
      '*': ' times ',
      '×': ' times ',
      '/': ' divided by ',
      '÷': ' divided by ',
      '^': ' to the power of ',
      '²': ' squared ',
      '³': ' cubed ',
      '√': ' square root of ',
      '<': ' is less than ',
      '>': ' is greater than ',
      '%': ' percent ',
      '(': ' open bracket ',
      ')': ' close bracket '
    };

    Object.keys(symbolMap).forEach(symbol => {
      const escapedSymbol = symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      converted = converted.replace(new RegExp(escapedSymbol, 'g'), symbolMap[symbol]);
    });

    const numberWords = {
      '1': 'one', '2': 'two', '3': 'three', '4': 'four', '5': 'five',
      '6': 'six', '7': 'seven', '8': 'eight', '9': 'nine', '10': 'ten',
      '11': 'eleven', '12': 'twelve', '13': 'thirteen', '14': 'fourteen', '15': 'fifteen',
      '16': 'sixteen', '17': 'seventeen', '18': 'eighteen', '19': 'nineteen', '20': 'twenty'
    };

    Object.keys(numberWords).forEach(num => {
      converted = converted.replace(new RegExp(`\\b${num}\\b`, 'g'), numberWords[num]);
    });

    converted = converted.replace(/\b([a-z])([a-z])\b/g, '$1 times $2');
    converted = converted.replace(/(\d)([a-zA-Z])/g, '$1 $2');
    converted = converted.replace(/\s+/g, ' ').trim();
    
    return converted;
  };

  const convertToSpeech = async (text) => {
    if (!text || text.trim().length === 0) {
      return text;
    }

    if (getCachedResult) {
      return getCachedResult;
    }

    setIsConverting(true);
    
    try {
      if (OPENAI_API_KEY && OPENAI_API_KEY !== 'your-openai-api-key-here') {
        const openAIResult = await convertWithOpenAI(text);
        setCachedResult(text, openAIResult);
        setIsConverting(false);
        return openAIResult;
      } else {
        throw new Error("API key not configured");
      }
    } catch (error) {
      const fallbackResult = convertWithFallback(text);
      setCachedResult(text, fallbackResult);
      setIsConverting(false);
      return fallbackResult;
    }
  };

  const handleAudioPlay = async () => {
    speechSynthesis.cancel();
    
    try {
      const readableText = await convertToSpeech(explanation);
      
      utteranceRef.current = new SpeechSynthesisUtterance(readableText);
      
      if (selectedVoice) {
        utteranceRef.current.voice = selectedVoice;
      }
      
      utteranceRef.current.rate = 0.9;
      utteranceRef.current.pitch = 1.1;
      utteranceRef.current.volume = 1;
      utteranceRef.current.lang = 'en-AU';
      
      utteranceRef.current.onstart = () => {
        setIsPlaying(true);
      };
      
      utteranceRef.current.onend = () => {
        setIsPlaying(false);
      };
      
      utteranceRef.current.onerror = (event) => {
        setIsPlaying(false);
      };
      
      speechSynthesis.speak(utteranceRef.current);
      
    } catch (error) {
      setIsConverting(false);
      const fallbackText = convertWithFallback(explanation);
      utteranceRef.current = new SpeechSynthesisUtterance(fallbackText);
      if (selectedVoice) utteranceRef.current.voice = selectedVoice;
      utteranceRef.current.rate = 0.9;
      utteranceRef.current.pitch = 1.1;
      utteranceRef.current.volume = 1;
      utteranceRef.current.lang = 'en-AU';
      utteranceRef.current.onstart = () => setIsPlaying(true);
      utteranceRef.current.onend = () => setIsPlaying(false);
      utteranceRef.current.onerror = () => setIsPlaying(false);
      speechSynthesis.speak(utteranceRef.current);
    }
  };

  const handleAudioStop = () => {
    speechSynthesis.cancel();
    setIsPlaying(false);
    setIsConverting(false);
  };

  useEffect(() => {
    if (!isOpen && (isPlaying || isConverting)) {
      handleAudioStop();
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      speechSynthesis.cancel();
      setIsConverting(false);
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center">
      <div className="w-full bg-[#75B954] text-white relative rounded-t-3xl flex flex-col items-center">
        <div className="w-full flex justify-between items-center px-6 pt-6 pb-2">
          <button
            onClick={isPlaying ? handleAudioStop : handleAudioPlay}
            className={`w-14 h-14 flex items-center justify-center rounded-full shadow-md hover:scale-105 transition-all duration-200 ${
              isConverting ? 'bg-blue-500' : 
              isPlaying ? 'bg-orange-500 animate-pulse' : 'bg-red-500'
            }`}
            aria-label={isConverting ? 'Converting...' : isPlaying ? 'Stop audio' : 'Play audio'}
            disabled={!selectedVoice || isConverting}
          >
            {isConverting ? (
              <Bot size={28} className="text-white animate-bounce" />
            ) : isPlaying ? (
              <Square size={28} className="text-white" />
            ) : (
              <Mic size={28} className="text-white" />
            )}
          </button>
          
          {isConverting && (
            <div className="flex items-center space-x-2 text-sm opacity-90 bg-blue-500 bg-opacity-20 px-3 py-1 rounded-full">
              <Bot size={16} className="animate-pulse" />
              <span>AI enhancing audio...</span>
            </div>
          )}
          
          <button
            onClick={onClose}
            className="w-16 h-16 transition-transform hover:scale-105"
            aria-label="Close modal"
          >
            <img
              src={Buttons.cross}
              alt="Close"
              className="h-14 object-contain"
            />
          </button>
        </div>
        
        <div className="px-20 pb-6 text-center flex items-center justify-center max-h-[70vh] overflow-y-auto">
          <p className="text-2xl leading-relaxed font-mali font-medium mb-20">
            {explanation}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExplanationModal;
